import React, { useState, useCallback } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import styled from '@emotion/styled'
import { EntityType, CollateralBasket, CollateralBasketType, ProductSector, WorkflowActions, Product } from 'api'
import { ModalWindow, ModalFooter, Groupbox } from 'view/components/panels'
import { DialogRow, DialogField } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { Tabs } from 'view/components/tabs'
import { CollateralBasketOverviewPanel } from './CollateralBasketOverviewPanel'
import { CollateralBasketProductsPanel } from './CollateralBasketProductsPanel'
import { CollateralBasketPermissionsPanel } from './CollateralBasketPermissionsPanel'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import {
  collateralBasketProductSectorIdsForTypeGetterSelector,
  createCollateralBasketByIdGetterSelector
} from 'selectors/collateralBasketSelectors'
import { Locale, Translation } from 'locale'
import { noop } from 'lodash'

export interface ViewCollateralBasketDialogProps {
  // Own Props
  entityId: string
  onClose?: () => unknown

  // State Props
  getCollateralBasketEntity: (basketId: string) => CollateralBasket | null
  getProductSector: (productSectorId: string) => ProductSector | null
  getProductSectorIdsForType: (basketType: CollateralBasketType) => string[]
  getProduct: (productId: string) => Product | null
}

const mapStateToProps = createStructuredSelector({
  getCollateralBasketEntity: createCollateralBasketByIdGetterSelector,
  getProductSector: createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
  getProductSectorIdsForType: collateralBasketProductSectorIdsForTypeGetterSelector,
  getProduct: createEntityGetterSelector(EntityType.PRODUCT)
})

const mapDispatchToProps = {
  onUpdate: WorkflowActions.createEntityUpdateReferenceData,
  onSubmitWorkflow: WorkflowActions.createSubmitWorkflow,
  onCancelWorkflow: WorkflowActions.createCancelWorkflow
}

const Content = styled(Groupbox)<{ width?: number }>`
  padding: 20px 20px 0 20px;
  min-height: 450px;
  overflow-y: auto;
  width: ${props => (props.width ? `${props.width}px` : '510px')};
`

export const ViewCollateralBasketDialogComponent: React.FunctionComponent<ViewCollateralBasketDialogProps> = props => {
  const { entityId, getCollateralBasketEntity, getProductSector, getProductSectorIdsForType, getProduct, onClose } = props

  const collateralBasket = getCollateralBasketEntity(entityId)

  const [selectedTab, setSelectedTab] = useState(Translation.TC_OVERVIEW)

  const onChangeTab = useCallback(
    (tab: Translation) => {
      setSelectedTab(tab)
    },
    [setSelectedTab]
  )

  const collateralBasketProductSector = getProductSector(collateralBasket?.productSectorId ?? '')
  const collateralBasketProduct = collateralBasketProductSector?.productId ? getProduct(collateralBasketProductSector.productId) : null
  const isGcBasket = collateralBasketProductSector?.basketType === CollateralBasketType.GC_BESPOKE

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose()
    }
  }, [onClose])

  const getSelectedTabContent = useCallback(
    (tab: Translation) => {
      if (!collateralBasket) return null

      switch (tab) {
        case Translation.TC_OVERVIEW:
          return (
            <Content>
              <CollateralBasketOverviewPanel
                collateralBasket={collateralBasket}
                collateralBasketProductSector={collateralBasketProductSector}
                collateralBasketProduct={collateralBasketProduct}
                getProductSectorIdsForType={getProductSectorIdsForType}
                validationIssues={[]}
                readOnly={true}
                onUpdate={noop}
                isNewCollateralBasket={false}
              />
            </Content>
          )
        case Translation.TC_PRODUCTS:
          return (
            <Content width={collateralBasket.collateralIds.length === 0 ? 540 : 1100}>
              <CollateralBasketProductsPanel
                collateralBasket={collateralBasket}
                collateralBasketProductSector={collateralBasketProductSector}
                readOnly={true}
                validationIssues={[]}
                onUpdate={noop}
                viewOnly={true}
              />
            </Content>
          )
        case Translation.TC_PERMISSIONS:
          return (
            <Content width={collateralBasket.tradingEntityIds.length === 0 ? 540 : 1030}>
              <CollateralBasketPermissionsPanel
                collateralBasket={collateralBasket}
                collateralBasketProductSector={collateralBasketProductSector}
                onUpdate={noop}
                readOnly={true}
                viewOnly={true}
              />
            </Content>
          )
        default:
          return <Content />
      }
    },
    [collateralBasket, collateralBasketProductSector, collateralBasketProduct, getProductSectorIdsForType]
  )

  if (!collateralBasket) {
    return null
  }

  const title = collateralBasket.name

  return (
    <ModalWindow title={title}>
      <Tabs
        dataTestId={'CollateralBasketDialog-Tabs'}
        selectedTab={selectedTab}
        onChangeTab={onChangeTab}
        tabs={[Translation.TC_OVERVIEW, Translation.TC_PRODUCTS, isGcBasket ? Translation.TC_PERMISSIONS : Translation.TC_EMPTY_HEADER]}
      />
      {getSelectedTabContent(selectedTab)}
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton dataTestId={'CollateralBasketDialog-TextButton-ButtonCancel'} type={TextButtonType.Primary} onClick={handleClose}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              dataTestId={'OrganisationDialog-TextButton-ButtonSave'}
              type={TextButtonType.Primary}
              disabled={true}
              onClick={noop}>
              {Locale.translate(Translation.TC_BUTTON_SAVE)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}

export const ViewCollateralBasketDialog = connect(mapStateToProps, mapDispatchToProps)(ViewCollateralBasketDialogComponent)
